import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { DateClickArg } from '@fullcalendar/interaction';
import { EventContentArg, EventApi, DatesSetArg, EventClickArg, EventInput } from '@fullcalendar/core';
import BaseCalendar from './BaseCalendar';
import { Evento } from '../../types/eventos';
import { Festivo } from '../../types/festivos';
import EventForm from '../forms/EventForm';
import EventDetail from '../eventos/EventDetail';
import { useQuery } from '@tanstack/react-query';
import { useCreateEventoMutation, useUpdateEventoMutation } from '../../services/hooks';
import { fetchEventosByDateRange } from '../../services/api'
import FestivosModal from '../modals/FestivosModal';
import { fetchFestivos } from '../../services/api/festivoService';
import { toast } from 'react-toastify';
import CompressedCalendar from './CompressedCalendar';
import PlantillasSemanales from './PlantillasSemanales';
import EventosMasivos from '../eventos/EventosMasivos';
import { TreePalm, Download, LayoutPanelTop, CalendarArrowUp, Filter, Cable, Printer, Plus, CalendarArrowDown, MoreHorizontal } from 'lucide-react'
import FiltroPlazas from '../plazas/FiltroPlazas';
import AsignarClientesMasivos from '../eventos/AsignarClientesMasivos';
import ImprimirPlazas from '../plazas/ImprimirPlazas';
import { useRole } from '../../hooks/useRole';
import EliminarEventosMasivos from '../eventos/EliminarEventosMasivos';
import { getEventBackgroundColor } from '../../utils/utils';
import useIsMobile from '../../services/hooks/useIsMobile';



const MainCalendar: React.FC = () => {
    const { isAdmin } = useRole();

    const [showForm, setShowForm] = useState(false);
    const [showDetail, setShowDetail] = useState(false);
    const [initialDate, setInitialDate] = useState('');
    const [initialStartTime, setInitialStartTime] = useState('');
    const [initialEndTime, setInitialEndTime] = useState('');
    const [selectedEvent, setSelectedEvent] = useState<Evento | null>(null);


    //rango de fechas para la consulta de eventos
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    //variable para modal de festivos
    const [showFestivosModal, setShowFestivosModal] = useState(false);

    // Variable para mostrar el calendario comprimido
    const [showCompressedCalendar, setShowCompressedCalendar] = useState(false);

    // Variable para mostrar las plantillas semanales
    const [showPlantillasSemanales, setShowPlantillasSemanales] = useState(false);

    // Variable para mostrar el modal de crear eventos masivos
    const [showEventosMasivos, setShowEventosMasivos] = useState(false);

    // Variable para mostrar el modal de eliminar eventos masivos
    const [showDeleteEventosMasivos, setShowDeleteEventosMasivos] = useState(false);

    // Variable para mostrar el modal de filtros de plazas
    const [showFiltroPlazas, setShowFiltroPlazas] = useState(false);

    // Variable para mostrar el modal de Asignar Clientes Masivos
    const [showAsignarClientesMasivos, setShowAsignarClientesMasivos] = useState(false);

    //Variable para mostrar el modal de Imprimir Plazas
    const [showImprimirPlazas, setShowImprimirPlazas] = useState(false);

    // D4 — desplegable "Acciones" de móvil (ver `acciones` más abajo).
    const [showAccionesMenu, setShowAccionesMenu] = useState(false);
    const accionesMenuRef = useRef<HTMLDivElement>(null);

    // D2 — al pulsar "Convertir en cliente" desde el listado de personas de
    // prueba se llega aqui con esa persona en el state. Se abre directamente la
    // busqueda de plazas, que es el primer paso de la inscripcion, y la persona
    // sigue viajando hasta el formulario.
    // D4 — en la rejilla de móvil cada clase mide ~110px de ancho: solo caben la
    // hora, el nivel y la ocupación. Sala e instructor están en el detalle.
    const isMobile = useIsMobile();

    const location = useLocation();
    const prospectAConvertir = (location.state as { prospect?: { id: string; name: string; phone: string; email: string } } | null)?.prospect;

    useEffect(() => {
        if (prospectAConvertir) setShowFiltroPlazas(true);
    }, [prospectAConvertir]);

    // Mismo patrón que el menú de sucursal y la hamburguesa de `Header.tsx`.
    useEffect(() => {
        const alPulsarFuera = (event: MouseEvent) => {
            if (accionesMenuRef.current && !accionesMenuRef.current.contains(event.target as Node)) {
                setShowAccionesMenu(false);
            }
        };
        document.addEventListener('mousedown', alPulsarFuera);
        return () => document.removeEventListener('mousedown', alPulsarFuera);
    }, []);

////////////////////////// CAPA DE SERVICIOS //////////////////////////

    // Consulta de eventos por rango de fechas
    const { 
            data: eventos, 
            // isLoading: isLoadingEventos, 
            // isError: isErrorEventos 
        } = useQuery<Evento[]>({
            queryKey: ['eventos', startDate, endDate],
            queryFn: () => fetchEventosByDateRange(startDate, endDate),
            enabled: !!startDate && !!endDate, // Solo ejecutar la consulta si las fechas están definidas
    });

    // console.log('eventos', eventos);

    //llamando a las mutaciones para crear, modificar o eliminar un nivel
    const {mutateAsync: createEvento } = useCreateEventoMutation(startDate, endDate);

    const {mutateAsync: updateEvento } = useUpdateEventoMutation(startDate, endDate);


    //Consulta de festivos
    const { 
            data: festivos, 
            // isLoading: isLoadingFestivos, 
            // isError: isErrorFestivos 
        } = useQuery<Festivo[]>({
            queryKey: ['festivos'],
            queryFn: fetchFestivos,
    });


//////////////////////// FIN CAPA DE SERVICIOS //////////////////////////



//////////////////////// FUNCIONES DEL CALENDARIO //////////////////////////

    // Función para manejar el cambio de fechas en el calendario
    // Permite saber las fechas de visualización actual para realizar consultas de eventos
    const handleDatesSet = (dateInfo: DatesSetArg) => {
        // console.log(dateInfo, "dateInfo");
        setStartDate(dateInfo.startStr);
        setEndDate(dateInfo.endStr);
        // const start = dateInfo.startStr;
        // const end = dateInfo.endStr;
        // alert(`start: ${startDate}, end: ${endDate}`);  
    };

    // Función para manejar el click en una fecha del calendario
    // Con esta función creamos nuevos eventos manualmente
    const handleDateClick = (info: DateClickArg) => {
        // console.log('info', info);
        const fechaSeleccionada = info.dateStr.split('T')[0];

        // console.log('fechaSeleccionada', fechaSeleccionada);
        // Verificar si la fecha seleccionada es un día festivo
        const esFestivo = festivos?.some(festivo => festivo.fecha.split('T')[0] === fechaSeleccionada);
        // console.log('esFestivo', esFestivo);
        if (esFestivo) {
            toast.warn('No se pueden crear eventos en días festivos.');
            return;
        }
        
        const fecha = info.dateStr.split('T')[0];
        const horaInicio = info.dateStr.split('T')[1]?.substring(0, 5);
        setInitialDate(fecha);
        setInitialStartTime(horaInicio ? horaInicio : '09:00');
        setInitialEndTime('10:00');
        if (horaInicio) {
            const [hours, minutes] = horaInicio.split(':');
            const endHour = (parseInt(hours) + 1).toString().padStart(2, '0');
            setInitialEndTime(`${endHour}:${minutes}`);
        } else {
            setInitialEndTime('10:00');
        }
        setShowForm(true);
    };


    // Función para manejar el click en un evento del calendario
    // Con esta función mostramos el detalle de un evento y permitimos editarlo
    const handleEventClick = (info: EventClickArg) => {
         // info.event es de tipo EventApi
        const eventApi: EventApi = info.event;
         // Castear extendedProps al tipo definido
        const extendedProps = eventApi.extendedProps as Evento

        setSelectedEvent(extendedProps);
        setShowDetail(true);
        // console.log('event', extendedProps);
    };


    // Función para guardar o editar un evento
    const handleSaveEditEvent = (newEvent: Evento) => {
        if(selectedEvent) {
            updateEvento({
                ...newEvent,
                // Si ya viene con actualizarFuturos, lo mantiene, de lo contrario lo establece en false
                actualizarFuturos: newEvent.actualizarFuturos || false
            });
        }else{
            createEvento(newEvent);
        }
        
        setShowForm(false);
    };

    // Función para cerrar el formulario de eventos
    const handleCloseForm = () => {
        setShowForm(false);
        setSelectedEvent(null);
    };


    // Función para cerrar el detalle de un evento
    const handleCloseDetail = () => {
        setShowDetail(false);
        setSelectedEvent(null);
    };


    // Función para editar un evento
    const handleEditEvent = () => {
        setShowDetail(false);
        setShowForm(true);
    };

    // Función para renderizar el contenido de los eventos
    const renderEventContent = (eventInfo: EventContentArg) => {
        if(eventInfo.event.extendedProps._id) {
            const backgroundColor = getEventBackgroundColor(eventInfo.event.extendedProps.nivel);
            const props = eventInfo.event.extendedProps;
            const ocupadas = props.clientesInscritos.length + (props.enlistedProspects?.length || 0);

            if (isMobile) {
                return (
                    <div className="h-full w-full overflow-hidden rounded px-1 py-0.5 leading-tight" style={{ backgroundColor }}>
                        {props.cancelado && (
                            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                                <p className="text-white text-[10px] font-bold">CANCELADA</p>
                            </div>
                        )}
                        <p className="text-[10px] font-semibold text-white">{eventInfo.timeText}</p>
                        <b className="block truncate text-[10px] text-slate-100">{eventInfo.event.title}</b>
                        <p className="text-[10px] text-white">
                            {ocupadas}/{props.maxClientes}
                            {(props.enlistedProspects?.length || 0) > 0 && (
                                <span className="ml-1 rounded bg-white/90 px-1 text-[9px] font-bold text-purple-700">P</span>
                            )}
                        </p>
                    </div>
                );
            }

            return (
                <div 
                    className="h-full w-full rounded-xl overflow-hidden p-2" 
                    style={{ backgroundColor }}
                >
                    {eventInfo.event.extendedProps.cancelado && (
                        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                            <p className="text-white text-lg font-bold">CANCELADO</p>
                        </div>
                    )}
                    <p className="text-xs font-semibold text-white">{eventInfo.timeText}</p>
                    <b className='text-slate-100 text-sm truncate block'>{eventInfo.event.title}</b>
                    <p className="text-xs truncate text-white">Sala: <strong>{eventInfo.event.extendedProps.sala}</strong></p>
                    {/* D2 — el contador suma clientes y personas de prueba: las dos
                        ocupan sitio en la sala. El distintivo avisa de un vistazo de
                        que ese día a esa hora hay alguien probando. */}
                    <p className="text-xs text-white flex items-center gap-1">
                        <span>
                            {eventInfo.event.extendedProps.clientesInscritos.length
                                + (eventInfo.event.extendedProps.enlistedProspects?.length || 0)}
                            /{eventInfo.event.extendedProps.maxClientes}
                        </span>
                        {(eventInfo.event.extendedProps.enlistedProspects?.length || 0) > 0 && (
                            <span
                                className="bg-white/90 text-purple-700 rounded px-1 text-[10px] font-bold"
                                title={`${eventInfo.event.extendedProps.enlistedProspects.length} persona(s) de prueba`}
                            >
                                PRUEBA {eventInfo.event.extendedProps.enlistedProspects.length}
                            </span>
                        )}
                    </p>
                </div>
            );
        }else{
            return (
                <div className='h-full w-full  rounded-xl overflow-hidden p-2'>
                    <p className='text-gray-700'>FESTIVO - {eventInfo.event.title}</p>
                </div>
            );
        }
    };

//////////////////////// FIN FUNCIONES DEL CALENDARIO //////////////////////////


/////////////////////// FUNCIONES DE FESTIVOS //////////////////////////


    const handleOpenModal = () => {
        setShowFestivosModal(true);
    };

    const handleCloseModal = () => {
        setShowFestivosModal(false);
    };

    // Convertir festivos a eventos de FullCalendar
    const festivoEvents = festivos?.map(festivo => ({
        id: festivo._id,
        title: festivo.descripcion || 'Festivo',
        start: festivo.fecha,
        allDay: true,
        display: 'background',
        backgroundColor: '#ff9f89', // Color para los festivos
        borderColor: '#ff9f89',
        textColor: '#000000',
    })) || [];

    // Combinar eventos normales y festivos
    const allEvents = [...(eventos || []), ...festivoEvents];

    // Cancelar eventos en días festivos (si no están cancelados)
    const cancelEventsOnFestivos = (festivosMap: Map<string, Festivo>, eventos: Evento[]) => {
        const eventosToUpdate = eventos.filter(evento => {
            const eventoDate = new Date(evento.fecha).toISOString().split('T')[0];
            return festivosMap.has(eventoDate) && !evento.cancelado;
        });
    
        if (eventosToUpdate.length > 0) {
            eventosToUpdate.forEach(evento => {
                updateEvento({ ...evento, cancelado: true });
            });
        }
    };

/////////////////////// FIN FUNCIONES DE FESTIVOS //////////////////////////

/////////////////////// EFECTOS //////////////////////////

    useEffect(() => {
        // console.log('eventos useEffect', eventos);
        // console.log('selectedEvent useEffect', selectedEvent);
        if(selectedEvent && eventos) {
            const adaptedEvent = eventos.find((evento) => evento._id === selectedEvent._id);
            // console.log('adaptedEvent', adaptedEvent);
            setSelectedEvent(adaptedEvent ?? null);
        }
    }, [eventos, selectedEvent]);


    useEffect(() => {
        if (festivos && eventos) {
            const festivosMap = new Map(festivos.map(festivo => [new Date(festivo.fecha).toISOString().split('T')[0], festivo]));
            cancelEventsOnFestivos(festivosMap, eventos);
        }
    }, [festivos]);

/////////////////////// FIN EFECTOS //////////////////////////

    // if (isLoadingEventos || isLoadingFestivos) {
    //     return <p>Cargando eventos...</p>;
    // }

    // if (isErrorEventos || isErrorFestivos) {
    //     return <p>Error cargando eventos</p>;
    // }

    // D4 — una sola lista para las dos maquetaciones. Antes eran ocho bloques
    // de JSX con la misma clase copiada a mano, asi que cualquier cambio habia
    // que hacerlo ocho veces. El orden es el que se ve en escritorio.
    const claseBotonAccion = 'text-white font-bold py-2 px-3 rounded bg-brand-primary hover:brightness-90 flex items-center gap-1 text-xs whitespace-nowrap';

    const acciones = [
        { id: 'festivos', label: 'Gestionar Festivos', icon: TreePalm, onClick: handleOpenModal, soloAdmin: true },
        { id: 'exportar', label: 'Exportar Calendario', icon: Download, onClick: () => setShowCompressedCalendar(true), soloAdmin: false },
        { id: 'plantillas', label: 'Plantillas Semanales', icon: LayoutPanelTop, onClick: () => setShowPlantillasSemanales(true), soloAdmin: true },
        { id: 'crear-masivos', label: 'Crear Eventos Masivos', icon: CalendarArrowUp, onClick: () => setShowEventosMasivos(true), soloAdmin: true },
        { id: 'eliminar-masivos', label: 'Eliminar Eventos Masivos', icon: CalendarArrowDown, onClick: () => setShowDeleteEventosMasivos(true), soloAdmin: true },
        { id: 'asignar-masivos', label: 'Asignar Clientes Masivos', icon: Cable, onClick: () => setShowAsignarClientesMasivos(true), soloAdmin: true },
        { id: 'nuevo-cliente', label: 'Nuevo Cliente', icon: Plus, onClick: () => setShowFiltroPlazas(true), soloAdmin: false },
        { id: 'imprimir', label: 'Imprimir Plazas', icon: Printer, onClick: () => setShowImprimirPlazas(true), soloAdmin: false },
    ].filter((accion) => isAdmin || !accion.soloAdmin);

    // En movil "Nuevo Cliente" se queda fuera del desplegable: es la accion del
    // dia a dia y la unica que usa quien no es administrador.
    const accionPrincipal = acciones.find((accion) => accion.id === 'nuevo-cliente');
    const accionesPlegadas = acciones.filter((accion) => accion.id !== 'nuevo-cliente');

    return (
        <div className="w-full mx-auto py-6 px-0 md:py-12 md:px-14">
            <h2 className="text-xl md:text-2xl font-bold text-center text-gray-800 mb-4 md:mb-6">
                Calendario
            </h2>
            {isMobile ? (
                // D4 — con sus etiquetas ("Asignar Clientes Masivos") ninguna de
                // estas acciones cabe al lado de otra a 375px, asi que las ocho
                // se apilaban en ocho filas. Solo queda fuera la de diario.
                <div className="flex items-center gap-2 py-2">
                    {accionPrincipal && (
                        <button className={claseBotonAccion} onClick={accionPrincipal.onClick}>
                            {accionPrincipal.label} <accionPrincipal.icon size={18} />
                        </button>
                    )}
                    <div className="relative" ref={accionesMenuRef}>
                        <button
                            className={claseBotonAccion}
                            onClick={() => setShowAccionesMenu((abierto) => !abierto)}
                            aria-haspopup="true"
                            aria-expanded={showAccionesMenu}
                        >
                            Acciones <MoreHorizontal size={18} />
                        </button>
                        {showAccionesMenu && (
                            // z-30: por encima de la rejilla de FullCalendar.
                            // Anclado a la derecha: alineado a la izquierda se
                            // salia 35px de la pantalla y cortaba las etiquetas.
                            <div className="absolute right-0 z-30 mt-1 w-60 rounded border border-gray-200 bg-white py-1 shadow-lg">
                                {accionesPlegadas.map((accion) => (
                                    <button
                                        key={accion.id}
                                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                                        onClick={() => { setShowAccionesMenu(false); accion.onClick(); }}
                                    >
                                        <accion.icon size={16} /> {accion.label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                <div className='flex flex-wrap justify-start py-2 gap-2'>
                    {acciones.map((accion) => (
                        <button
                            key={accion.id}
                            className={`${claseBotonAccion} my-2`}
                            onClick={accion.onClick}
                        >
                            {accion.label} <accion.icon size={18} />
                        </button>
                    ))}
                </div>
            )}
            <BaseCalendar
                events={allEvents as unknown as EventInput[]}
                onDatesSet={handleDatesSet}
                onDateClick={handleDateClick}
                onEventClick={handleEventClick}
                editable={true}
                selectable={true}
                eventContent={renderEventContent}
            />
            {showForm && (
                <EventForm
                    initialDate={initialDate}
                    initialStartTime={initialStartTime}
                    initialEndTime={initialEndTime}
                    onSave={handleSaveEditEvent}
                    onClose={handleCloseForm}
                    event={selectedEvent}
                />
            )}
            {showDetail && selectedEvent && (
                <EventDetail
                    event={selectedEvent}
                    onClose={handleCloseDetail}
                    onEditEvent={handleEditEvent}
                    startDate={startDate}
                    endDate={endDate}
                />
            )}
            {showFestivosModal && <FestivosModal onClose={handleCloseModal} />}
            {showCompressedCalendar && (
                <CompressedCalendar
                    eventos={eventos || []}
                    festivos={festivos || []}
                    onClose={() => setShowCompressedCalendar(false)}
                    viewStartDate={startDate}
                    viewEndDate={endDate}
                />
            )}
            {showPlantillasSemanales && (
                <PlantillasSemanales onClose={() => setShowPlantillasSemanales(false)} />
            )}
            {showEventosMasivos && (
                <EventosMasivos 
                    onClose={() => setShowEventosMasivos(false)} 
                    startDate={startDate}
                    endDate={endDate}
                    festivos={festivos || []}
                />
            )}
            {showDeleteEventosMasivos && (
                <EliminarEventosMasivos 
                    onClose={() => setShowDeleteEventosMasivos(false)} 
                    startDate={startDate}
                    endDate={endDate}
                />
            )}
            {showFiltroPlazas && (
                <FiltroPlazas 
                    onClose={() => setShowFiltroPlazas(false)} 
                    prospect={prospectAConvertir}
                />
            )}
            {showImprimirPlazas && (
                <ImprimirPlazas 
                    onClose={() => setShowImprimirPlazas(false)} 
                />
            )}
            {showAsignarClientesMasivos && (
                <AsignarClientesMasivos 
                    onClose={() => setShowAsignarClientesMasivos(false)} 
                />
            )}

        </div>
    );
};

export default MainCalendar;